import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { RowData } from '../App';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './DataGrid.css'; // Import your custom CSS
import { CellValueChangedEvent, ColDef, GridApi, GridReadyEvent, RowDragEndEvent } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'; 
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import { trackEvent } from '../utils/analytics';
ModuleRegistry.registerModules([AllCommunityModule]);

interface DataGridProps {
  rowData: RowData[];
  columnDefs: ColDef[];
  onRowDataChange?: (newRowData: RowData[]) => void | Promise<void>;
  isLogoBlock?: boolean;
  logoOptions?: string[];
  isSingleColumn?: boolean;
  isSongBlock?: boolean;
  blockId?: string;
  blockName?: string;
}

const DataGrid: React.FC<DataGridProps> = ({
  rowData,
  columnDefs,
  onRowDataChange,
  isLogoBlock,
  isSongBlock,
  logoOptions,
  isSingleColumn,
  blockId,
  blockName,
}) => {
  const [gridApi, setGridApi] = useState<GridApi<RowData> | null>(null);
  const [isUrlDialogOpen, setIsUrlDialogOpen] = useState<boolean>(false);
  const [urlInputValue, setUrlInputValue] = useState<string>('');
  const [urlInputError, setUrlInputError] = useState<string>('');
  const [pendingEdit, setPendingEdit] = useState<{ rowIndex: number; field: string; oldValue: string } | null>(null);

  useEffect(() => {
    trackEvent('data_grid_viewed', {
      block_id: blockId,
      block_name: blockName,
      row_count: rowData.length,
      column_count: columnDefs.length,
      is_logo_block: Boolean(isLogoBlock),
      is_song_block: Boolean(isSongBlock),
      is_single_column: Boolean(isSingleColumn),
    }, {
      dedupeKey: `${blockId ?? 'no_block'}:${rowData.length}:${columnDefs.length}`,
      dedupeMs: 2000,
    });
  }, [blockId, blockName, columnDefs.length, isLogoBlock, isSingleColumn, isSongBlock, rowData.length]);
    
  // Determine which data to use
  const displayColumnDefs = useMemo<ColDef[]>(() => {
    const withRowDragHandle = (defs: ColDef[]): ColDef[] =>
      defs.map((colDef: ColDef, index: number) => (index === 0 ? { ...colDef, rowDrag: true } : colDef));

    const base: ColDef[] = Array.isArray(columnDefs) ? [...columnDefs] : [];
    if ((!isLogoBlock && !isSingleColumn) || isSongBlock) return withRowDragHandle(base);

    if (isSingleColumn) {
      // Make the 1st column disabled
      return withRowDragHandle([{...base[0], headerName: 'Label - Not Shown', editable: false, cellClass: 'ag-cell-not-editable', headerClass: 'ag-header-cell-not-editable'}, base[1]]);
    }

    // From here it's just for logo blocks
    const enterUrlLabel: string = 'Enter URL...';
    const providedValues: string[] = Array.isArray(logoOptions) ? logoOptions : [];
    const allowedValues: string[] = [
      enterUrlLabel,
      ...providedValues.sort(),
    ];

    // For logo blocks, use only the URL/filename column
    // Find the label column: prefer 'col1' or header 'Label', fallback to index 1
    let labelColumnIndex: number = base.findIndex((col: ColDef) => !!col && (col.field === 'col1'));
    if (labelColumnIndex === -1) {
      labelColumnIndex = base.findIndex((col: ColDef) => !!col && typeof col.headerName === 'string' && (col.headerName as string).toLowerCase() === 'label');
    }
    if (labelColumnIndex === -1 && base.length >= 2) {
      labelColumnIndex = 1;
    }

    if (labelColumnIndex >= 0 && base[labelColumnIndex]) {
      const urlColumn: ColDef = {
        ...base[labelColumnIndex],
        headerName: 'URL/Filename',
        editable: true,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: allowedValues },
        sortable: false,
        filter: false,
      };

      // For logo blocks, show only the URL column
      return withRowDragHandle([urlColumn]);
    }

    return withRowDragHandle(base);
  }, [columnDefs, isLogoBlock, isSingleColumn, isSongBlock, logoOptions]);
  
  // useEffect(() => {
  //   // console.log('DataGrid received data:', { 
  //   //   rowCount: rowData.length, 
  //   //   columnCount: columnDefs.length,
  //   // });
    
  //   // If we have a grid API, tell it to refresh when data changes
  //   /*
  //   if (gridApi) {
  //     gridApi.setColumnDefs(displayColumnDefs);
  //     gridApi.setRowData(displayRowData);
  //   }
  //     */
  // }, [rowData, columnDefs, gridApi, displayColumnDefs, displayRowData]);



  const onUpdateData = (event: CellValueChangedEvent<RowData>) => {
    // Special handling for logo blocks: allow a custom URL entry via a sentinel option
    if (isLogoBlock) {
      const enterUrlLabel: string = 'Enter URL...';
      const valueField: string | undefined = typeof displayColumnDefs[0]?.field === 'string' ? (displayColumnDefs[0]?.field as string) : undefined;
      const eventField: string | undefined = typeof event.colDef.field === 'string' ? (event.colDef.field as string) : undefined;
      const isValueColumn: boolean = !!valueField && !!eventField && (valueField === eventField);
      if (isValueColumn && event.newValue === enterUrlLabel) {
        trackEvent('data_grid_logo_url_prompt_opened', {
          block_id: blockId,
          block_name: blockName,
          row_index: event.rowIndex ?? 0,
        });
        // Revert the temporary select value and open dialog for URL entry
        const oldValue: string = typeof event.oldValue === 'string' ? event.oldValue : '';
        if (eventField) {
          event.node.setDataValue(eventField, oldValue);
        }
        setPendingEdit({ rowIndex: event.rowIndex ?? 0, field: eventField || valueField || 'col1', oldValue });
        setUrlInputValue('');
        setUrlInputError('');
        setIsUrlDialogOpen(true);
        return;
      }
    }

    const updatedRowData: RowData[] = [...rowData];
    const rowIndex: number = event.rowIndex ?? 0;
    const originalRow: RowData = rowData[rowIndex] ?? {};
    let mergedRow: RowData = { ...originalRow, ...event.data };
    
    updatedRowData[rowIndex] = mergedRow;
    trackEvent('data_grid_cell_edited', {
      block_id: blockId,
      block_name: blockName,
      row_index: rowIndex,
      field_name: event.colDef.field ?? event.column.getColId(),
      block_type: isLogoBlock ? 'logo' : isSongBlock ? 'song' : isSingleColumn ? 'single_cell' : 'standard',
    }, {
      dedupeKey: `${blockId ?? 'no_block'}:${event.colDef.field ?? event.column.getColId()}:${rowIndex}`,
    });
    const result = onRowDataChange?.(updatedRowData);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onRowDataChange:', error);
      });
    }
    
    // Maintain focus position after cell value change
    setTimeout(() => {
      if (gridApi && event.rowIndex !== undefined && event.rowIndex !== null) {
        const currentRowIndex = event.rowIndex;
        const currentColField = event.column.getColId();
        
        // Try to focus the same cell, or move to next row if at the end
        if (currentRowIndex < updatedRowData.length - 1) {
          // Move to next row, same column
          gridApi.setFocusedCell(currentRowIndex + 1, currentColField);
        } else {
          // Stay in the same cell if at the last row
          gridApi.setFocusedCell(currentRowIndex, currentColField);
        }
        
        // Ensure the focused cell is visible
        gridApi.ensureIndexVisible(currentRowIndex);
      }
    }, 50);
  }

  const handleUrlDialogClose = () => {
    setIsUrlDialogOpen(false);
    setPendingEdit(null);
    setUrlInputValue('');
    setUrlInputError('');
  };

  const handleUrlDialogSave = () => {
    const input: string = urlInputValue.trim();
    const isValidUrl: boolean = /^https?:\/\/.+/.test(input);
    if (!isValidUrl) {
      setUrlInputError('Please enter a valid URL starting with http:// or https://');
      return;
    }
    if (!pendingEdit) {
      handleUrlDialogClose();
      return;
    }

    const { rowIndex, field } = pendingEdit;

    // Update underlying data and notify parent
    const updatedRowData: RowData[] = [...rowData];
    const originalRow: RowData = updatedRowData[rowIndex] ?? {};
    let mergedRow: RowData = { ...originalRow, [field]: input };
    
    // Ensure col2 has default values for logo blocks
    if (isLogoBlock && !('col2' in mergedRow)) {
      mergedRow = { ...mergedRow, col2: JSON.stringify({ scale: 100, xOffset: 0, yOffset: 0, autoCrop: false }) };
    }
    
    updatedRowData[rowIndex] = mergedRow;
    trackEvent('data_grid_logo_url_saved', {
      block_id: blockId,
      block_name: blockName,
      row_index: rowIndex,
    });
    const result = onRowDataChange?.(updatedRowData);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onRowDataChange:', error);
      });
    }

    // Also update the grid cell immediately for responsiveness
    if (gridApi) {
      const node = gridApi.getDisplayedRowAtIndex(rowIndex);
      if (node) node.setDataValue(field, input);
    }

    handleUrlDialogClose();
  };

  const addRow = () => {
    const newRow: RowData = {};
    // Initialize all columns with empty values
    displayColumnDefs.forEach(colDef => {
      const field = typeof colDef.field === 'string' ? colDef.field : undefined;
      if (field) {
        // Set default values for logo blocks (serialized as JSON)
        if (isLogoBlock && field === 'col2') {
          newRow[field] = JSON.stringify({ scale: 100, xOffset: 0, yOffset: 0, autoCrop: false });
        } else {
          newRow[field] = '';
        }
      }
    });
    
    const updatedRowData = [...rowData, newRow];
    trackEvent('data_grid_row_added', {
      block_id: blockId,
      block_name: blockName,
      next_row_count: updatedRowData.length,
    });
    const result = onRowDataChange?.(updatedRowData);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onRowDataChange:', error);
      });
    }
    
    // Focus on the newly added row after a short delay to ensure the grid has updated
    setTimeout(() => {
      if (gridApi) {
        const newRowIndex = updatedRowData.length - 1;
        const firstField: string | undefined = typeof displayColumnDefs[0]?.field === 'string' ? displayColumnDefs[0].field : undefined;
        if (firstField) {
          gridApi.setFocusedCell(newRowIndex, firstField);
        }
        gridApi.ensureIndexVisible(newRowIndex);
      }
    }, 100);
  };

  const removeSelectedRows = () => {
    if (!gridApi) return;
    
    const selectedNodes = gridApi.getSelectedNodes();
    if (selectedNodes.length === 0) return;
    
    const selectedIndexes = selectedNodes.map((node: any) => node.rowIndex).sort((a: number, b: number) => b - a);
    const updatedRowData = [...rowData];
    
    // Remove rows in reverse order to maintain correct indices
    selectedIndexes.forEach((index: number) => {
      updatedRowData.splice(index, 1);
    });

    trackEvent('data_grid_rows_deleted', {
      block_id: blockId,
      block_name: blockName,
      deleted_count: selectedIndexes.length,
      next_row_count: updatedRowData.length,
    });
    
    const result = onRowDataChange?.(updatedRowData);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onRowDataChange:', error);
      });
    }
  };



  const onRowDragEnd = (event: RowDragEndEvent<RowData>): void => {
    if (!onRowDataChange) return;

    const reorderedRowData: RowData[] = [];
    event.api.forEachNode((node) => {
      if (node.data) {
        reorderedRowData.push(node.data);
      }
    });

    if (reorderedRowData.length !== rowData.length) return;

    trackEvent('data_grid_rows_reordered', {
      block_id: blockId,
      block_name: blockName,
      row_count: reorderedRowData.length,
    }, {
      dedupeKey: `${blockId ?? 'no_block'}:${reorderedRowData.length}`,
    });

    const result: void | Promise<void> = onRowDataChange(reorderedRowData);
    if (result instanceof Promise) {
      result.catch((error: unknown) => {
        console.error('Error in onRowDataChange:', error);
      });
    }
  };

  const onGridReady = (params: GridReadyEvent<RowData>): void => {
    setGridApi(params.api);
    
    // Force the grid to render properly
    setTimeout(() => {
      params.api.sizeColumnsToFit();
      params.api.refreshCells({ force: true });
      
      // Sometimes a manual redraw helps with display issues
      window.dispatchEvent(new Event('resize'));
    }, 100);
  };

  return (
    <>
      <Dialog open={isUrlDialogOpen} onClose={handleUrlDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Enter logo URL</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL (http:// or https://)"
            type="url"
            fullWidth
            value={urlInputValue}
            onChange={(e) => setUrlInputValue(e.target.value)}
            error={!!urlInputError}
            helperText={urlInputError || 'Enter a direct URL to the logo image or resource'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUrlDialogClose}>Cancel</Button>
          <Button onClick={handleUrlDialogSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      <div 
        className="ag-theme-alpine data-grid-container" 
        style={{ 
          height: '400px', 
          width: '100%', 
          border: 'none', // Remove inline border
          display: 'block',
          overflow: 'visible'
        }}
      >
        <AgGridReact<RowData>
          rowData={rowData}
          columnDefs={displayColumnDefs}
          pagination={false}
          rowDragManaged={true}
          animateRows={true}
          defaultColDef={{
            flex: 1,
            minWidth: 100,
            resizable: true,
            editable: true,
            filter: false,
            sortable: false,
          }}
          onGridReady={onGridReady}
          onCellValueChanged={onUpdateData}
          onRowDragEnd={onRowDragEnd}
          suppressCellFocus={false}
          suppressRowHoverHighlight={false}
          enableCellTextSelection={true}
          rowSelection="multiple"
          suppressClipboardApi={false}
          suppressContextMenu={true}
          sideBar={false}
          cellSelection={true}
          suppressMultiSort={true}
        />
      </div>
      
      <div className="data-grid-table" style={{ marginTop: '10px', fontSize: '12px' }}>
        <div style={{ color: '#666' }}>
          {(rowData.length > 0 ? `Showing ${rowData.length} rows from Excel` : 'No Excel data to display')}
        </div>
        
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button 
            onClick={addRow}
            className="data-grid-action-btn"
            title="Add new row"
          >
            + Add Row
          </button>
          <button 
            onClick={removeSelectedRows}
            className="data-grid-action-btn delete"
            title="Delete selected rows"
          >
            × Delete Selected
          </button>
        </div>
      </div>
    </>
  );
};

export default DataGrid;
